import { makeStyles } from '@mui/styles';
import Badge from 'apollo-react/components/Badge';
import Button from 'apollo-react/components/Button';
import IconButton from 'apollo-react/components/IconButton';
import Table, { compareStrings } from 'apollo-react/components/Table';
import Tooltip from 'apollo-react/components/Tooltip';
import EllipsisHorizontal from 'apollo-react-icons/EllipsisHorizontal';
import _ from 'lodash';
import { Resizable } from 're-resizable';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { batch, useDispatch, useSelector } from 'react-redux';

import { RuleSectionModal } from './RuleSectionModal';
import { createStringSearchFilter } from '../../../components/SavedSearchesTable/utils';
import TextFieldFilter from '../../../components/TableFilters/TextInputFilter';
import { useFormSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getIsRtlLanguage } from '../../../selectors/AuthSelectors';
import { getUITA } from '../../../utils/commonUtils';
import { scrollIntoEditor } from '../../../utils/domUtils';
import {
  changeIsDockToBottom,
  setHeightOfTable,
  setSelectedControl,
  setWidthOfTable,
} from '../../actions/editor.actions';
import { ROOT, SOURCE_TYPES } from '../../constants/form-manager.constants';
import { INTERACTION_RULES_SUPPORTED_TYPES } from '../../constants/runtime.constants';
import {
  getEntityDefinition,
  getIsDockToBottom,
  getTableHeight,
  getTableWidth,
  getTranslations,
} from '../../selectors/common.selectors';
import {
  getIsFormModelEmpty,
  getOriginalModel,
  getPreProcessedRules,
  getSelectedControlId,
} from '../../selectors/editor.selectors';
import { equalityFn, getPropTranslation, updateRulesScripts } from '../../utils/form-manager.utils';

const useStyles = makeStyles({
  rulesTableContainer: {
    position: 'relative',
    zIndex: 9,
    '& div[class*="MuiToolbar-root"]': {
      height: 50,
    },
    '& div[class*="MuiToolbar-root"] + div': {
      height: ({ height }) => (height ? height - 50 : '100%'),
    },
  },
  titleText: {
    paddingTop: '5px',
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '30px',
    padding: '5px',
  },
  badgeContainer: {
    overflow: 'visible !important',
    marginRight: 15,
  },
  badge: {
    zIndex: 0,
  },
  tableContainer: {
    margin: '0 16px',
    height: 'calc(100% - 80px)',
    '& div': {
      height: '100%',
    },
    '& table': {
      height: '100%',
    },
  },

  btnTableContainer: {
    display: 'flex',
    alignItems: 'center',
    right: '5px',
  },

  dockToBottom: {
    width: '24px !important',
    height: '19px',
    borderTop: '5px solid #595959',
    borderLeft: '5px solid #595959',
    borderRight: '5px solid #595959',
    borderBottom: '9px solid #595959',
    cursor: 'pointer',
    '&.active': {
      borderTop: '5px solid #0768fd',
      borderLeft: '5px solid #0768fd',
      borderRight: '5px solid #0768fd',
      borderBottom: '9px solid #0768fd',
    },
  },
  undockTableContainer: {
    padding: '5px 0',
    height: 'calc(100% - 25px)',
    '& div': {
      height: '100%',
    },
    '& table': {
      height: '100%',
    },
  },
  undockContainer: {
    width: '20px',
    height: '15px',
    marginRight: 10,
    position: 'relative',
    cursor: 'pointer',
    '&.active': {
      '&>div:first-child': {
        border: '3px solid #0768fd',
      },
      '&>div:last-child': {
        background: '#0768fd',
      },
    },
  },
  undockTopElement: {
    background: 'white',
    width: '16px',
    height: '12px',
    border: '3px solid #595959',
    marginLeft: '9px',
    position: 'relative',
    bottom: '2px',
    zIndex: 2,
    '&>div:first-child': {
      content: '',
      position: 'absolute',
      top: -3,
      left: -4,
      width: '1px',
      height: '12px',
      background: 'white',
    },
    '&>div:last-child': {
      content: '',
      position: 'absolute',
      bottom: -4,
      left: -4,
      width: '10px',
      height: '1px',
      background: 'white',
    },
  },
  undockBottomElement: {
    width: '16px',
    height: '10px',
    background: '#595959',
    position: 'absolute',
    top: '7px',
    left: 0,
    zIndex: 1,
  },
  returnContentTable: {
    border: '2px solid lightgrey',
    height: '100%',
    backgroundColor: 'white',
  },
});

const ModalCell = ({ row, column }) => {
  const classes = useStyles();
  const { setControlRow, setModalType, setOpenModal, setModalTitle, translatedControlName } = row;
  const { accessor, header } = column;

  const providedValue =
    accessor === 'Conditions' || accessor === 'mapping'
      ? row[accessor] ?? []
      : accessor === 'targetFilter' || accessor === 'sourceFilter'
      ? row[accessor]?.Conditions ?? []
      : [];

  const handleButtonClick = (e) => {
    e.stopPropagation();
    switch (accessor) {
      case 'Conditions':
      case 'targetFilter':
      case 'sourceFilter': {
        setModalType('condition');
        break;
      }
      case 'mapping': {
        setModalType('mapping');
        break;
      }
      default:
        break;
    }
    setControlRow(providedValue);
    setModalTitle({ translatedControlName, header });
    setOpenModal(true);
  };

  return (
    <Badge
      badgeContent={Array.isArray(providedValue) ? providedValue.length : 0}
      className={classes.badge}
    >
      <IconButton
        onClick={(event) => {
          handleButtonClick(event);
        }}
        className={classes.badgeContainer}
        size="small"
        disabled={!providedValue.length}
      >
        <EllipsisHorizontal />
      </IconButton>
    </Badge>
  );
};

const columns = [
  {
    header: 'Scope',
    accessor: 'scope',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('scope'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Control triggering the Rule',
    accessor: 'translatedControlName',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('translatedControlName'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Condition',
    accessor: 'Conditions',
    customCell: ModalCell,
    align: 'center',
  },
  {
    header: 'Action',
    accessor: 'dummyType',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('dummyType'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Target',
    accessor: 'translatedTargetMapping',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('translatedTargetMapping'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Target Filter',
    accessor: 'targetFilter',
    customCell: ModalCell,
    align: 'center',
  },
  {
    header: 'Value Type',
    accessor: 'sourceType',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('sourceType'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Value',
    accessor: 'dummySource',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('dummySource'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Value Filter',
    accessor: 'sourceFilter',
    customCell: ModalCell,
    align: 'center',
  },
  {
    header: 'Mapping',
    accessor: 'mapping',
    customCell: ModalCell,
    align: 'center',
  },
];

function filterRowsBySelectedControl(formControl) {
  return (row) =>
    _.isNil(formControl) ||
    row.targetMapping === `${formControl?.entityName}.${formControl?.fieldName}` ||
    formControl?.parent === ROOT;
}

const getSourceType = ({ sourceType }) =>
  sourceType && sourceType === SOURCE_TYPES.DATASOURCE
    ? SOURCE_TYPES.DATASOURCE
    : SOURCE_TYPES.FORMULA;

const RulesList = ({ height }) => {
  const classes = useStyles({ height });
  const rows = useFormSelector(getPreProcessedRules, {}, equalityFn);
  const selectedControlId = useFormSelector(getSelectedControlId);
  const formControl = useFormSelector(getOriginalModel, {
    id: selectedControlId,
  });
  const translateString = useTranslation();
  const entityDefinition = useFormSelector(getEntityDefinition);
  const translations = useFormSelector(getTranslations);
  const showRulesTable = useFormSelector(getIsFormModelEmpty);
  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const tableRowHeadRef = useRef(null);
  const divRef = useRef(null);

  const processedRules = useMemo(
    () =>
      rows.map((item) => {
        const { source, controlName, targetMapping, type, dummyParameter } = item;
        const isInteractionAction = INTERACTION_RULES_SUPPORTED_TYPES.includes(type);

        return {
          ...item,
          type,
          sourceType: isInteractionAction ? '' : getSourceType(item),
          source: isInteractionAction ? '' : updateRulesScripts(source, true),
          translatedControlName: getPropTranslation(entityDefinition, translations, controlName),
          translatedTargetMapping: isInteractionAction
            ? dummyParameter
            : getPropTranslation(entityDefinition, translations, targetMapping),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(rows)]
  );

  const [filteredRows, setFilteredRows] = useState(processedRules);
  const [showAllRules, setShowRules] = useState(true);
  const [ruleTableTitle, setTitle] = useState('All Rules');
  const [isOpen, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [controlRow, setControlRow] = useState([]);
  const [modalTitle, setModalTitle] = useState({});
  const [position, setPosition] = useState({ x: 520, y: -550 });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isResize, setResize] = useState(false);
  const [isTouchOnScrollbar, setIsTouchOnScrollbar] = useState(false);
  const [boundingClientRect, setBoundingClientRect] = useState(null);

  const isDockToBottom = useSelector(getIsDockToBottom);
  const tableWidth = useSelector(getTableWidth);
  const tableHeight = useSelector(getTableHeight);

  const dispatch = useDispatch();

  const SIDEBAR_WIDTH = 64;
  const HEADER_HEIGHT = 150;
  const BASE_BOTTOM_OFFSET = -400;

  useEffect(() => {
    if (showAllRules) {
      setFilteredRows(processedRules);
    } else {
      setFilteredRows(processedRules.filter(filterRowsBySelectedControl(formControl)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(processedRules), showAllRules]);

  useEffect(() => {
    setFilteredRows(processedRules.filter(filterRowsBySelectedControl(formControl)));
    if (formControl?.parent === ROOT) {
      setTitle('All');
      setShowRules(true);
    } else if (!formControl?.fieldName) {
      setTitle(formControl?.controlType);
      setShowRules(false);
    } else {
      setTitle(`${formControl?.fieldName}`);
      setShowRules(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedControlId]);

  useEffect(() => {
    if (isRtlLanguage) {
      const applyAttributeTimer = setTimeout(() => {
        return tableRowHeadRef.current?.columnHeaderRootRef.current.setAttribute('dir', 'ltr');
      }, 1000);
      return () => clearTimeout(applyAttributeTimer);
    }
  }, [isRtlLanguage]);

  useEffect(() => {
    const defineScrollbar = (e) =>
      e.offsetX > e.target.clientWidth || e.offsetY > e.target.clientHeight;

    const onMouseDownHandler = (e) => {
      const isScrollbar = defineScrollbar(e);
      if (isScrollbar && !isDockToBottom) {
        setIsTouchOnScrollbar(true);
      }
    };

    const onMouseUpHandler = (e) => {
      const isScrollbar = defineScrollbar(e);
      if (isScrollbar && !isDockToBottom) {
        setIsTouchOnScrollbar(false);
      }
    };

    window.addEventListener('mousedown', onMouseDownHandler);
    window.addEventListener('mouseup', onMouseUpHandler);

    return () => {
      window.removeEventListener('mousedown', onMouseDownHandler);
      window.removeEventListener('mouseup', onMouseUpHandler);
    };
  }, [isDockToBottom]);

  const selectCurrentController = (row) => {
    dispatch(setSelectedControl(row.controlId, true));
    scrollIntoEditor(row.controlId);
  };

  const handleShowButtonClick = () => {
    setFilteredRows(processedRules);
    setShowRules(true);
    setTitle('All');
  };

  const onChange = useCallback(
    (value) => {
      dispatch(changeIsDockToBottom(value));
    },
    [dispatch]
  );

  const onClickUndockCallback = useCallback(() => {
    onChange(false);

    batch(() => {
      dispatch(setWidthOfTable(600));
      dispatch(setHeightOfTable(400));
    });
  }, [dispatch, onChange]);

  const onClickDockCallback = useCallback(() => {
    onChange(true);

    batch(() => {
      dispatch(setWidthOfTable('auto'));
      dispatch(setHeightOfTable('auto'));
    });
  }, [dispatch, onChange]);

  const CustomHeader = () => {
    const classes = useStyles();

    return (
      <div className={classes.titleContent}>
        <div>
          <h4 className={classes.titleText} ui-ta={getUITA('RulesList', 'Title')}>
            {`${translateString('Rules')} -  ${translateString(ruleTableTitle)}`}
          </h4>
        </div>
        <div className={classes.btnTableContainer}>
          <Tooltip title="Undock into separate window">
            <div
              role="none"
              className={`${classes.undockContainer} ${!isDockToBottom && 'active'}`}
              onClick={onClickUndockCallback}
              onKeyDown={onClickUndockCallback}
            >
              <div className={classes.undockTopElement}>
                <div />
                <div />
              </div>
              <div className={classes.undockBottomElement} />
            </div>
          </Tooltip>
          <Tooltip title="Dock to bottom">
            <div
              role="none"
              className={`${classes.dockToBottom} ${isDockToBottom && 'active'}`}
              onClick={onClickDockCallback}
              onKeyDown={onClickDockCallback}
            />
          </Tooltip>
          <div>
            {!showAllRules && (
              <Button
                className={classes.titleActionButton}
                onClick={() => handleShowButtonClick()}
                variant="text"
                style={{ marginRight: 10 }}
                ui-ta={getUITA('RulesList', 'ShowAllRulesBtn')}
              >
                {translateString('Show All')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const returnedContent = useMemo(() => {
    return (
      <div ref={divRef} className={classes.returnContentTable}>
        <div className={classes.titleContent}>
          <div>
            <h4 className={classes.titleText} ui-ta={getUITA('RulesList', 'Title')}>
              {`${translateString('Rules')} -  ${translateString(ruleTableTitle)}`}
            </h4>
          </div>
          <div className={classes.btnTableContainer}>
            <Tooltip title="Undock into separate window">
              <div
                role="none"
                className={`${classes.undockContainer} ${!isDockToBottom && 'active'}`}
                onClick={onClickUndockCallback}
                onKeyDown={onClickUndockCallback}
              >
                <div className={classes.undockTopElement}>
                  <div />
                  <div />
                </div>
                <div className={classes.undockBottomElement} />
              </div>
            </Tooltip>
            <Tooltip title="Dock to bottom">
              <button
                className={`${classes.dockToBottom} ${isDockToBottom && 'active'}`}
                onClick={onClickDockCallback}
              />
            </Tooltip>
            <div>
              {!showAllRules && (
                <Button
                  className={classes.titleActionButton}
                  onClick={() => handleShowButtonClick()}
                  variant="text"
                  style={{ marginRight: 10 }}
                  ui-ta={getUITA('RulesList', 'ShowAllRulesBtn')}
                >
                  {translateString('Show All')}
                </Button>
              )}
            </div>
          </div>
        </div>
        <div
          className={(classes.tableContainer, !isDockToBottom ? classes.undockTableContainer : '')}
        >
          <Table
            subtitle=""
            columns={columns}
            rows={filteredRows.map((row) => ({
              ...row,
              setOpenModal,
              setControlRow,
              setModalType,
              setModalTitle,
              onClick: () => selectCurrentController(row),
            }))}
            showFilterIcon
            rowProps={{ 'ui-ta': getUITA('RulesList', 'Row') }}
            initialSortedColumn="scope"
            initialSortOrder="asc"
            rowsPerPageOptions={[5, 10, 15]}
            stripedRows
            hasScroll
            tablePaginationProps={{
              dir: 'ltr',
              pageNumberInputProps: {
                InputProps: {
                  inputProps: {
                    'aria-label': 'Page Number',
                  },
                },
              },
            }}
            ref={tableRowHeadRef}
          />
          {isOpen && (
            <RuleSectionModal
              open={isOpen}
              modalType={modalType}
              modalTitle={modalTitle}
              rows={controlRow}
              onCloseHandler={setOpenModal}
            />
          )}
        </div>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    classes.returnContentTable,
    classes.titleContent,
    classes.titleText,
    classes.btnTableContainer,
    classes.undockContainer,
    classes.undockTopElement,
    classes.undockBottomElement,
    classes.dockToBottom,
    classes.titleActionButton,
    classes.tableContainer,
    classes.undockTableContainer,
    translateString,
    ruleTableTitle,
    isDockToBottom,
    onClickUndockCallback,
    onClickDockCallback,
    showAllRules,
    filteredRows,
    isOpen,
    modalType,
    modalTitle,
    controlRow,
  ]);

  const onDragCallback = useCallback(() => {
    setBoundingClientRect(divRef?.current?.getBoundingClientRect());
  }, []);

  const onStopCallback = useCallback(
    (e) => {
      let xx = position.x;
      let yy = position.y;

      if (boundingClientRect?.x + tableWidth > e?.view?.innerWidth) {
        xx = e?.view?.innerWidth - tableWidth - 100;
      } else if (boundingClientRect?.x < SIDEBAR_WIDTH) {
        xx = 0;
      } else {
        xx = position.x + e.clientX - startPosition.x;
      }

      if (boundingClientRect?.y + tableHeight > e?.view?.innerHeight) {
        yy = BASE_BOTTOM_OFFSET;
      } else if (boundingClientRect?.y - HEADER_HEIGHT < 0) {
        yy = -e?.view?.innerHeight + HEADER_HEIGHT;
      } else {
        yy = position.y + e.clientY - startPosition.y;
      }

      setPosition({
        x: xx,
        y: yy,
      });
    },
    [
      BASE_BOTTOM_OFFSET,
      boundingClientRect?.x,
      boundingClientRect?.y,
      position.x,
      position.y,
      startPosition.x,
      startPosition.y,
      tableHeight,
      tableWidth,
    ]
  );

  return (
    showRulesTable &&
    (!isDockToBottom ? (
      <div className={classes.rulesTableContainer} ui-ta={getUITA('RulesList')}>
        <Draggable
          style={classes.draggable}
          axis={isResize ? 'none' : 'both'}
          disabled={isResize}
          position={position}
          onStart={(e) =>
            !isResize && !isTouchOnScrollbar && setStartPosition({ x: e.clientX, y: e.clientY })
          }
          onDrag={onDragCallback}
          onStop={(e) => {
            !isResize && !isTouchOnScrollbar && onStopCallback(e);
          }}
        >
          <Resizable
            onResizeStart={() => {
              setResize(true);
              setPosition(position);
            }}
            enable={{
              right: true,
              bottom: true,
              bottomRight: true,
            }}
            minWidth={300}
            minHeight={200}
            size={{ width: tableWidth, height: tableHeight }}
            onResizeStop={(e, direction, ref, d) => {
              dispatch(setWidthOfTable(tableWidth + d.width));
              dispatch(setHeightOfTable(tableHeight + d.height));
              setResize(false);
            }}
          >
            {returnedContent}
          </Resizable>
        </Draggable>
      </div>
    ) : (
      <div className={classes.rulesTableContainer} ui-ta={getUITA('RulesList')}>
        <Table
          subtitle=""
          columns={columns}
          rows={filteredRows.map((row) => ({
            ...row,
            setOpenModal,
            setControlRow,
            setModalType,
            setModalTitle,
            onClick: () => selectCurrentController(row),
          }))}
          showFilterIcon
          rowProps={{ 'ui-ta': getUITA('RulesList', 'Row') }}
          initialSortedColumn="scope"
          initialSortOrder="asc"
          rowsPerPageOptions={[5, 10, 15]}
          stripedRows
          hasScroll
          CustomHeader={CustomHeader}
          tablePaginationProps={{
            dir: 'ltr',
            pageNumberInputProps: {
              InputProps: {
                inputProps: {
                  'aria-label': 'Page Number',
                },
              },
            },
          }}
          ref={tableRowHeadRef}
        />
        {isOpen && (
          <RuleSectionModal
            open={isOpen}
            modalType={modalType}
            modalTitle={modalTitle}
            rows={controlRow}
            onCloseHandler={setOpenModal}
          />
        )}
      </div>
    ))
  );
};

export default RulesList;
